import { useCallback } from 'react';

import type { z } from 'zod';

import type { Locale } from '@change-corgi/config/locales';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageData, useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { useLocale } from '#src/app/shared/hooks/l10n';
import { useTrackExperimentExposureOnMount } from '#src/app/shared/hooks/tracking';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';

import { useGenerateAiDraft } from './hooks/useGenerateAiDraft';
import { schema } from './schema';

type FormData = z.infer<typeof schema>;
type Experiment = ReturnType<typeof useStartAPetitionPageData>['data']['experiments']['sapPersonalStoryPrompt'];

type UsePersonalPromptReturn = ModelHookResult<
	{
		schema: typeof schema;
		defaultValues: FormData;
		loading: boolean;
		error: Error | null;
		experiment: Experiment;
	},
	{
		back: () => void;
		submit: (formData: FormData) => void;
		persistDraft: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
	}
>;

// The Variant 1/Variant 2 copy only exists in English. GrowthBook's dashboard targeting rule
// already restricts assignment to these locales, but that config lives outside git — this is a
// second, code-level guard so a future targeting change can't show untranslated English copy (or
// misattribute `$exposure`) to a non-en-locale user.
const EN_LOCALES: ReadonlySet<Locale> = new Set<Locale>(['en-US', 'en-CA', 'en-GB', 'en-AU']);

function gateToLocale(experiment: Experiment, locale: Locale): Experiment {
	if (EN_LOCALES.has(locale)) return experiment;
	// Force Control so PersonalPrompt renders the right copy, and clear `experimentName` so
	// useTrackExperimentExposure's own "no distinct tracking key" no-op suppresses `$exposure` —
	// without this, a real Variant 1/2 assignment outside the approved locales would render Control
	// but still emit `$exposure` for the variant, corrupting experiment analytics.
	return { ...experiment, variation: 'Control', experimentName: '' };
}

export function usePersonalPrompt(): UsePersonalPromptReturn {
	const { data, actions } = useStartAPetitionPageState();
	const { data: pageData } = useStartAPetitionPageData();
	const track = useTracking();
	const locale = useLocale();

	const experiment = gateToLocale(pageData.experiments.sapPersonalStoryPrompt, locale);
	useTrackExperimentExposureOnMount(experiment);

	const { mutate, error, mutationState } = useGenerateAiDraft();

	const back = useCallback(() => {
		void track('sap_step_back', { step: 'personal_story' });
		actions.send({ type: 'back' });
	}, [actions, track]);

	const persistDraft = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			const personalStory = event.target.value.trim();
			const userInput = { personalStory };
			actions.send({ type: 'submit', draft: { input: userInput }, target: 'self' });
		},
		[actions],
	);

	return {
		data: {
			loading: isLoading(mutationState) || isLoaded(mutationState),
			schema,
			defaultValues: { personalStory: data.draft?.input?.personalStory ?? '' },
			error,
			experiment,
		},
		actions: {
			back,
			submit: mutate,
			persistDraft,
		},
	};
}
